import type {
  ISizeFormGroup,
  ISizeSelectedState,
} from '../StoreConfirmSizes/helpers/storeConfirmSizes.types';
import type { ISizeOrderCounts } from '@/common/interfaces/store.interface';
import { isGroupOrderStorePastCloseDate } from '@/utilities/OrderUtils';

export const LIVE_STORE_ORDER_STATUS = 1;

export const isStoreDetailsLiveForEdit = (
  status: number | string | null | undefined,
  groupOrderDate?: string | null
): boolean => {
  if (Number(status) !== LIVE_STORE_ORDER_STATUS) {
    return false;
  }

  return !isGroupOrderStorePastCloseDate(groupOrderDate);
};

export const applySizeOrderLocksToSizeGroup = (
  group: ISizeFormGroup,
  sizeOrderCounts: ISizeOrderCounts
): ISizeFormGroup => {
  const orderItemId = Number(group.orderItemId);
  const countsForItem = sizeOrderCounts[orderItemId] ?? {};

  const sizesSelected = Object.entries(group.sizesSelected).reduce<
    Record<string, ISizeSelectedState>
  >((acc, [label, size]) => {
    const orderCount = countsForItem[label] ?? 0;
    const lockedByExistingOrder = orderCount > 0;

    acc[label] = {
      ...size,
      selected: lockedByExistingOrder ? true : size.selected,
      disabled: lockedByExistingOrder ? true : size.disabled,
      lockedByExistingOrder,
    };

    return acc;
  }, {});

  return {
    ...group,
    sizesSelected,
  };
};

export const countUnlockedSelectedSizes = (
  sizesSelected: Record<string, ISizeSelectedState>
): number =>
  Object.values(sizesSelected).filter(
    (size) => size.selected && !size.lockedByExistingOrder && !size.disabled
  ).length;

export const canToggleSizeSelection = (
  sizesSelected: Record<string, ISizeSelectedState>,
  sizeLabel: string,
  nextSelected: boolean
): boolean => {
  const size = sizesSelected[sizeLabel];
  if (!size || size.lockedByExistingOrder || size.disabled) {
    return false;
  }

  if (nextSelected) {
    return true;
  }

  const unlockedSelectedCount = countUnlockedSelectedSizes(sizesSelected);
  if (size.selected && unlockedSelectedCount <= 1) {
    return false;
  }

  return true;
};
